import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { Payload } from '@atlaskit/rovo-triggers/types';
import { getRovoProductFromExtensionType } from '../utils/getRovoProductFromExtensionType';
import {
	getRovoAgentId,
	DEFAULT_AGENT_REFERENCE,
} from '../../../web-client/queries/getRovoAgentId';
import type { OpenRovoPayload } from '..';
import { ReactRelayContext } from 'react-relay';
import { useContext } from 'react';

const ROVO_TOPIC = 'ai-mate';
const FORGE_SOURCE = 'forge-bridge-open-rovo';
const ROVO_EVENT_TYPE = 'chat-new';

const makeForgeAgentExternalConfigReference = (
	appId: string,
	installationId: string,
	environmentId: string,
	agentKey: string,
) => {
	return `${installationId}_ari:cloud:ecosystem::extension/${appId}/${environmentId}/static/${agentKey}`;
};

export function useRovoAgentsChat(
	extensionType: string,
	appId: string,
	cloudId: string | undefined,
	installationId: string,
	environmentId: string,
) {
	const publish = usePublish(ROVO_TOPIC);
	const relayContext = useContext(ReactRelayContext);
	const relayEnvironment = relayContext?.environment;

	return {
		openRovo: async ({
			agentKey,
			agentName,
			prompt,
			sourceAppId,
			sourceInstallationId,
			sourceEnvironmentId,
		}: OpenRovoPayload): Promise<false | undefined> => {
			const product = getRovoProductFromExtensionType(extensionType);

			if (!cloudId || !product) {
				return false;
			}

			// Use sourceAppId if provided (from embedded macro forwarding), otherwise use the captured appId
			const effectiveAppId = sourceAppId || appId;
			const effectiveInstallationId = sourceInstallationId || installationId;
			const effectiveEnvironmentId = sourceEnvironmentId || environmentId;

			const isForgeAgent = agentName && agentKey;
			const publishEventDetails = {
				type: ROVO_EVENT_TYPE,
				product,
				source: FORGE_SOURCE,
			};

			let agentIdConfig;
			if (isForgeAgent) {
				agentIdConfig = {
					agentExternalConfigReference: makeForgeAgentExternalConfigReference(
						effectiveAppId,
						effectiveInstallationId,
						effectiveEnvironmentId,
						agentKey,
					),
				};
			} else {
				// If agent name is not provided use default rovo chat agent
				const agentId =
					agentName && !agentKey
						? await getRovoAgentId({
								agentAppId: effectiveAppId,
								cloudId,
								input: { name: agentName },
								product,
								agentKey,
								relayEnvironment,
							})
						: DEFAULT_AGENT_REFERENCE;
				agentIdConfig = {
					agentId,
				};
			}

			publish({
				...publishEventDetails,
				data: {
					dialogues: [],
					...agentIdConfig,
					...(prompt ? { prompt } : {}),
				},
			} as Payload);
		},
	};
}
